
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users } from "lucide-react";
import { EnrolledUser } from "@/types/attendance";
import { AttendanceTable } from "./AttendanceTable";
import { BulkActions } from "./BulkActions";
import { useState } from "react";
import { useAttendanceMutation } from "@/hooks/useAttendanceMutation";
import { SearchFilter } from "./SearchFilter";

interface AttendanceContainerProps {
  enrolledUsers: EnrolledUser[] | undefined;
  classId: string | undefined;
  selectedDate: string;
}

export const AttendanceContainer = ({ enrolledUsers, classId, selectedDate }: AttendanceContainerProps) => {
  const { mutate: updateAttendance, isPending: isUpdating } = useAttendanceMutation();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOption, setFilterOption] = useState<"all" | "present" | "absent" | "penalized">("all");

  // Filter users based on search term and filter option
  const filteredUsers = enrolledUsers?.filter(user => {
    const userName = `${user.profiles.first_name || ''} ${user.profiles.last_name || ''} ${user.profiles.username || ''}`.toLowerCase();
    const matchesSearch = userName.includes(searchTerm.toLowerCase());
    
    switch (filterOption) {
      case "present":
        return matchesSearch && user.attendanceRecord?.attended === true;
      case "absent":
        return matchesSearch && user.attendanceRecord?.attended === false;
      case "penalized":
        return matchesSearch && user.attendanceRecord?.penalized === true;
      default:
        return matchesSearch;
    }
  });

  const handleBulkAction = (action: 'present' | 'absent') => {
    if (!enrolledUsers || !classId) return;
    
    enrolledUsers.forEach(user => {
      updateAttendance({
        classId,
        userId: user.user_id,
        attended: action === 'present',
        penalized: false,
        selectedDate
      });
    });
  };

  return (
    <Card className="bg-[#293233] dark:bg-[#293233] forest:bg-black/10 sunset:bg-black/10 ocean:bg-black/10  border-[#00C1D6]/30 sunset:bg-black/20 sunset:border-black text-white">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Users className="h-5 w-5 mr-2" />
          Alumnos Inscritos ({enrolledUsers?.length || 0})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!enrolledUsers ? (
          <div className="animate-pulse flex items-center justify-center h-40">
            <div className="rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00C1D6] animate-spin"></div>
          </div>
        ) : (
          <>
            <BulkActions 
              enrolledUsers={enrolledUsers} 
              classId={classId} 
              selectedDate={selectedDate} 
              onBulkAction={handleBulkAction}
              isUpdating={isUpdating}
            />
            
            <SearchFilter 
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              filterOption={filterOption}
              onFilterChange={setFilterOption}
            />
            
            <AttendanceTable 
              enrolledUsers={filteredUsers || []} 
              classId={classId} 
              selectedDate={selectedDate} 
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};
